import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Equipment = () => {
  const { t } = useTranslation();
  const [equipment, setEquipment] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'GPS Tracker',
    quantity: 0,
    available: 0,
    price: 0
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    try {
      const res = await axios.get(`${API}/equipment`);
      setEquipment(res.data);
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/equipment/${editingId}`, formData);
        toast.success('Оборудването бе актуализирано!');
      } else {
        await axios.post(`${API}/equipment`, formData);
        toast.success('Оборудването бе добавено!');
      }
      fetchEquipment();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Сигурни ли сте?')) {
      try {
        await axios.delete(`${API}/equipment/${id}`);
        toast.success('Оборудването бе изтрито!');
        fetchEquipment();
      } catch (error) {
        toast.error('Грешка при изтриване');
      }
    }
  };

  const handleEdit = (equip) => {
    setFormData(equip);
    setEditingId(equip.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'GPS Tracker',
      quantity: 0,
      available: 0,
      price: 0
    });
    setEditingId(null);
  };

  const getTypeIcon = (type) => {
    const icons = {
      'GPS Tracker': '📍',
      'Camera': '📷',
      'Sensor': '🔧'
    };
    return icons[type] || '📦';
  };

  return (
    <div className="page-container" data-testid="equipment-page">
      <div className="page-header">
        <h1 className="page-title">{t('equipment')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-equipment-btn">
              <Plus size={20} /> {t('add')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? t('edit') : t('add')} Оборудване</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="equipment-form" data-testid="equipment-form">
              <Input
                placeholder="Име на оборудване (напр. GPS Tracker FMC130)"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
              <Select value={formData.type} onValueChange={(val) => setFormData({...formData, type: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GPS Tracker">GPS Tracker</SelectItem>
                  <SelectItem value="Camera">Camera</SelectItem>
                  <SelectItem value="Sensor">Sensor</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                placeholder="Общо количество (брой в склад)"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value)})}
                required
              />
              <Input
                type="number"
                placeholder="Налични бройки (неизползвани)"
                value={formData.available}
                onChange={(e) => setFormData({...formData, available: parseInt(e.target.value)})}
                required
              />
              <Input
                type="number"
                step="0.01"
                placeholder="Единична цена (лв за 1 бр.)"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                required
              />
              <div className="form-actions">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" data-testid="save-equipment-btn">{t('save')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="equipment-grid">
        {equipment.map(equip => (
          <div key={equip.id} className="equipment-card" data-testid={`equipment-card-${equip.id}`}>
            <div className="equipment-card-header">
              <div className="equipment-icon">{getTypeIcon(equip.type)}</div>
              <div>
                <h3>{equip.name}</h3>
                <p className="equipment-type">{equip.type}</p>
              </div>
            </div>

            <div className="equipment-card-body">
              <div className="equipment-info-row">
                <span>Количество:</span>
                <span>{equip.quantity}</span>
              </div>
              <div className="equipment-info-row">
                <span>Налични:</span>
                <span className={equip.available > 0 ? 'available-yes' : 'available-no'}>
                  {equip.available}
                </span>
              </div>
              <div className="equipment-info-row">
                <span>Цена:</span>
                <span className="equipment-price">{equip.price} лв</span>
              </div>
            </div>

            <div className="equipment-card-footer">
              <Button variant="ghost" size="sm" onClick={() => handleEdit(equip)} data-testid={`edit-equipment-${equip.id}`}>
                <Edit size={16} /> {t('edit')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(equip.id)} data-testid={`delete-equipment-${equip.id}`}>
                <Trash2 size={16} /> {t('delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Equipment;
